import React from 'react';
import styled from 'styled-components';
import memoize from 'lodash/memoize';
import qs from 'query-string';
import { hexToRgba } from '../colorConversion';
import { Type, black, spacer16, spacer8, white, gray50, } from '@spotify-internal/encore-web';
import { formatText } from '../utils';
import { makeImageSizeUrl, makeWebpUrl, makeJpgUrl } from '.';
import { EMPTY_IMAGE } from './constants';
import { TWO_COLUMN_MAX_WIDTH } from '../Components';
const StyledImage = styled.img ``;
const StyledImageWithPlaceholder = styled(StyledImage) `
  &.lazyloading {
    filter: blur(8px);
  }
  &.lazyloaded {
    filter: none;
    transition: 1s cubic-bezier(0.215, 0.61, 0.355, 1);
  }
`;
const CaptionText = styled(Type.p).attrs({
    variant: 'finale',
}) `
  position: absolute;
  bottom: ${spacer16};
  right: ${spacer16};
  text-align: right;
  background-color: ${hexToRgba(black, 0.5)};
  width: fit-content;
  padding: 0 5px 0 5px;
  color: ${white};
  @media (max-width: ${TWO_COLUMN_MAX_WIDTH}) {
    bottom: ${spacer8};
    right: ${spacer8};
  }
`;
export const lightCaptionStyle = {
    position: 'relative',
    color: gray50,
    background: white,
    textAlign: 'center',
};
export const ContentfulImage = (props) => {
    var _a, _b, _c, _d, _e;
    const { asset, caption, imageStyle = {}, captionStyle = {}, imageComponent, fit, focus, format, lazyLoad = true, sizes = [
        [480, 480],
        [768, 768],
        [1280, 1280],
        [1600, 1600],
    ], children, allowVideo = true, videoOptions = {
        autoPlay: true,
        playsInline: true,
        loop: true,
        muted: true,
    }, } = props;
    const pictureRef = React.useRef(null);
    if (allowVideo && ((_b = (_a = asset === null || asset === void 0 ? void 0 : asset.file) === null || _a === void 0 ? void 0 : _a.contentType) === null || _b === void 0 ? void 0 : _b.startsWith('video/'))) {
        return (React.createElement("video", { ...videoOptions, title: asset.description },
            React.createElement("source", { src: asset.file.url, type: asset.file.contentType })));
    }
    if (!((_d = (_c = asset === null || asset === void 0 ? void 0 : asset.file) === null || _c === void 0 ? void 0 : _c.contentType) === null || _d === void 0 ? void 0 : _d.startsWith('image/'))) {
        console.error('Invalid asset', asset === null || asset === void 0 ? void 0 : asset.title, (_e = asset === null || asset === void 0 ? void 0 : asset.file) === null || _e === void 0 ? void 0 : _e.contentType);
        return React.createElement(React.Fragment, null);
    }
    const contentType = asset.file.contentType;
    const { url, query } = qs.parseUrl(asset.file.url);
    const assetWidth = props.assetWidth || asset.file.details.image.width;
    const assetHeight = props.assetHeight || asset.file.details.image.height;
    const description = asset.description;
    if (fit) {
        query.fit = fit;
    }
    if (focus) {
        query.f = focus;
    }
    if (format) {
        query.fm = format[0];
        if (format.length > 1) {
            query.fl = format[1];
        }
    }
    if (props.assetWidth) {
        query.w = `${assetWidth}`;
    }
    if (props.assetHeight) {
        query.h = `${assetHeight}`;
    }
    const baseImageUrl = `${url}?${qs.stringify(query)}`;
    const baseWebpImageUrl = makeWebpUrl(baseImageUrl);
    const size = memoize((w) => makeImageSizeUrl(baseImageUrl, w, undefined), w => w);
    const sizeWebp = memoize((w) => makeImageSizeUrl(baseWebpImageUrl, w, undefined), w => w);
    let { placeholder = true } = props;
    if (placeholder === true) {
        placeholder = makeImageSizeUrl(makeJpgUrl(baseImageUrl, undefined, 0), 12, 10);
    }
    const ImageComponent = imageComponent || (placeholder ? StyledImageWithPlaceholder : StyledImage);
    const fallbackSrcSet = [...sizes]
        .map(([q, w]) => `${size(w)} ${q}w`)
        .join(', ');
    const getMediaQuery = (ms) => {
        const i = sizes.indexOf(ms) - 1;
        const min = i < 0 ? '' : `(min-width: ${sizes[i][0] + 1}px) and`;
        return `${min} (max-width: ${ms[1]}px)`.trim();
    };
    const srcSetProp = lazyLoad ? 'data-srcset' : 'srcSet';
    const makeSource = (mediaQuery, ctype, srcSet) => (React.createElement("source", { media: mediaQuery, type: ctype, ...{ [srcSetProp]: srcSet } }));
    const pictureContent = (React.createElement(React.Fragment, null,
        assetWidth &&
            sizes.map((ms) => (React.createElement(React.Fragment, { key: `${[ms[0], ms[1]].join('x')}` },
                makeSource(getMediaQuery(ms), 'image/webp', `${sizeWebp(ms[1])} 1x, ${sizeWebp(assetWidth && Math.min(assetWidth, ms[1] * 2))} 2x`),
                makeSource(getMediaQuery(ms), contentType, `${size(ms[1])} 1x, ${size(Math.min(assetWidth, ms[1] * 2))} 2x`)))),
        React.createElement(ImageComponent, { ...{ [srcSetProp]: fallbackSrcSet }, srcSet: lazyLoad ? placeholder || EMPTY_IMAGE : '', "data-sizes": "auto", alt: description, style: { display: 'block', ...imageStyle }, className: lazyLoad ? 'lazyload' : '' }),
        caption && (React.createElement(CaptionText, { style: captionStyle, dangerouslySetInnerHTML: { __html: formatText(caption) } }))));
    const picture = (React.createElement("picture", { ref: pictureRef },
        pictureContent,
        children));
    return React.createElement(React.Fragment, null, picture);
};
